import React, { useState, useMemo, useCallback } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { availableLanguages } from '../../locales';
import useCooldown from '../vedic/shared/hooks/useCooldown';
import deriveReading from '../vedic/prashnavali/rama/utils/deriveReading';
import LandingView from '../vedic/prashnavali/rama/LandingView';
import GridView from '../vedic/prashnavali/rama/GridView';
import ResultCard from '../vedic/prashnavali/rama/ResultCard';
import ShareImagePreview from '../vedic/prashnavali/rama/ShareImagePreview';
import shalakaData from '../vedic/prashnavali/rama/data/shalaka.json';
import { fetchPanchangaOnce } from '../vedic/prashnavali/shared/hooks/usePanchangaNow';
import { isQ3Enabled } from '../vedic/prashnavali/shared/utils/featureFlags';

const COOLDOWN_STORAGE_KEY = 'prashnavali_rama_last_query';
const COOLDOWN_DURATION_MS = 60 * 60 * 1000;

const PageContainer = styled.main`
  min-height: 100vh;
  background: #FFFDF7;
  padding: 24px 20px 60px;

  @media (max-width: ${p => p.theme.breakpoints.tablet}px) {
    padding: 16px 16px 40px;
  }
`;

const TopBar = styled.div`
  max-width: 720px;
  margin: 0 auto ${p => p.theme.spacing[4]}px;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const BackLink = styled.button`
  background: none;
  border: none;
  color: #8B6914;
  font-size: 14px;
  cursor: pointer;
  padding: 0;
  &:hover { text-decoration: underline; }
`;

const LangToggle = styled.div`
  display: flex;
  gap: 4px;
`;

const LangBtn = styled.button`
  padding: 6px 14px;
  border: 1px solid ${p => p.active ? '#DAA520' : 'rgba(218,165,32,0.25)'};
  background: ${p => p.active ? '#DAA520' : 'transparent'};
  color: ${p => p.active ? '#fff' : '#8B6914'};
  border-radius: 4px;
  font-size: 13px;
  font-weight: ${p => p.active ? 600 : 400};
  cursor: pointer;
  transition: all 0.2s;
  font-family: 'Noto Sans Tamil', sans-serif;
`;

const RamaShalaka = () => {
  const navigate = useNavigate();
  const [view, setView] = useState('landing');
  const [lang, setLang] = useState('ta');
  const [selectedCellIndex, setSelectedCellIndex] = useState(null);
  const [sharePreviewOpen, setSharePreviewOpen] = useState(false);
  const [panchangaSnapshot, setPanchangaSnapshot] = useState(null);

  const cooldown = useCooldown({
    storageKey: COOLDOWN_STORAGE_KEY,
    durationMs: COOLDOWN_DURATION_MS,
  });

  const reading = useMemo(() => {
    if (selectedCellIndex === null) return null;
    return deriveReading(selectedCellIndex, shalakaData);
  }, [selectedCellIndex]);

  const handleBegin = useCallback(() => {
    if (cooldown.active) return;
    setView('grid');
  }, [cooldown.active]);

  const handleCellTapped = useCallback((cellIndex) => {
    setSelectedCellIndex(cellIndex);
    setView('result');
    if (isQ3Enabled()) {
      fetchPanchangaOnce().then((snap) => setPanchangaSnapshot(snap));
    }
  }, []);

  const handleReturn = useCallback(() => {
    if (selectedCellIndex !== null) {
      cooldown.startCooldown(selectedCellIndex);
    }
    setSelectedCellIndex(null);
    setPanchangaSnapshot(null);
    setView('landing');
  }, [selectedCellIndex, cooldown]);

  const handleViewLastReading = useCallback(() => {
    if (cooldown.lastReadingCellIndex !== null) {
      setSelectedCellIndex(cooldown.lastReadingCellIndex);
      setView('result');
    }
  }, [cooldown.lastReadingCellIndex]);

  return (
    <PageContainer>
      <TopBar>
        <BackLink onClick={() => navigate('/vedic-tools')}>
          &larr; {lang === 'ta' ? 'கருவிகள்' : 'Tools'}
        </BackLink>
        <LangToggle>
          {availableLanguages.map(l => (
            <LangBtn key={l.code} active={lang === l.code} onClick={() => setLang(l.code)}>
              {l.label}
            </LangBtn>
          ))}
        </LangToggle>
      </TopBar>

      {view === 'landing' && (
        <LandingView
          onBegin={handleBegin}
          onViewLastReading={handleViewLastReading}
          cooldown={cooldown}
          lang={lang}
        />
      )}

      {view === 'grid' && (
        <GridView
          data={shalakaData}
          onCellTapped={handleCellTapped}
          lang={lang}
        />
      )}

      {view === 'result' && reading && (
        <ResultCard
          pathCells={reading.pathCells}
          verse={reading.verse}
          grid={shalakaData.grid}
          gridTa={shalakaData.grid_ta}
          onShare={() => setSharePreviewOpen(true)}
          onReturn={handleReturn}
          lang={lang}
          panchangaSnapshot={panchangaSnapshot}
        />
      )}

      {sharePreviewOpen && reading && (
        <ShareImagePreview
          open={sharePreviewOpen}
          onClose={() => setSharePreviewOpen(false)}
          pathCells={reading.pathCells}
          verse={reading.verse}
          grid={shalakaData.grid}
          gridTa={shalakaData.grid_ta}
          lang={lang}
        />
      )}
    </PageContainer>
  );
};

export default RamaShalaka;
